import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Car, LogOut, Plus, Calendar, MapPin, User, Clock, DollarSign, CreditCard, XCircle,
  FileText, Download, CheckCircle, AlertCircle, Loader2, Menu, X, ClipboardList
} from "lucide-react";
import { DashboardSkeleton } from "../components/DashboardSkeleton";

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("bookings");
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => { fetchBookings(); fetchInvoices(); }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get("/bookings");
      setBookings(response.data);
    } catch (error) { toast.error("Failed to load bookings"); }
    finally { setLoading(false); }
  };

  const fetchInvoices = async () => {
    try {
      const response = await api.get("/invoices/my-invoices");
      setInvoices(response.data);
    } catch (error) { console.log("Failed to load invoices:", error); }
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    try {
      await api.post(`/bookings/${bookingId}/cancel`);
      toast.success("Booking cancelled");
      fetchBookings();
    } catch (error) { toast.error("Failed to cancel booking"); }
  };

  const handlePayment = async (bookingId) => {
    try {
      const response = await api.post("/payments/checkout", { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = response.data.url;
    } catch (error) { toast.error("Failed to initiate payment"); }
  };

  const handleInvoicePayment = async (invoiceId) => {
    try {
      const response = await api.post("/payments/invoice-checkout", { invoice_id: invoiceId, origin_url: window.location.origin });
      window.location.href = response.data.url;
    } catch (error) { toast.error("Failed to initiate payment"); }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    setDownloadingPdf(invoiceId);
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (error) { toast.error("Failed to download invoice"); }
    finally { setDownloadingPdf(null); }
  };

  const getStatusBadge = (status) => {
    const styles = { pending: "status-pending", confirmed: "status-confirmed", dispatched: "status-dispatched", in_progress: "status-in_progress", completed: "status-completed", cancelled: "status-cancelled" };
    return <Badge className={`${styles[status]} capitalize`}>{status.replace("_", " ")}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const styles = { pending: "payment-pending", paid: "payment-paid", failed: "payment-failed" };
    return <Badge className={`${styles[status]} capitalize`}>{status}</Badge>;
  };

  const upcomingBookings = bookings.filter(b => ["pending", "confirmed", "dispatched"].includes(b.status));
  const pastBookings = bookings.filter(b => ["completed", "cancelled"].includes(b.status));
  const pendingInvoices = invoices.filter(i => i.status === "sent" || i.status === "overdue");
  const paidInvoices = invoices.filter(i => i.status === "paid");

  if (loading) return <DashboardSkeleton role="client" />;

  const navItems = [
    { id: "bookings", icon: ClipboardList, label: "My Bookings" },
    { id: "invoices", icon: FileText, label: "Invoices", badge: pendingInvoices.length || null },
    { id: "new", icon: Plus, label: "Book a Transfer", link: "/booking/new" },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="client-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#111114] border-b border-white/[0.06]">
        <div className="flex items-center justify-between p-4">
          <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-8 w-auto" />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white/70">
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-[#111114]/98 backdrop-blur-lg pt-16">
          <nav className="p-4 space-y-1">
            {navItems.map(item => (
              item.link ? (
                <Link key={item.id} to={item.link} onClick={() => setMobileMenuOpen(false)} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/50 hover:bg-white/[0.04]">
                  <item.icon className="h-5 w-5" />{item.label}
                </Link>
              ) : (
                <button key={item.id} onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === item.id ? "bg-[#C8A95B]/10 text-[#C8A95B]" : "text-white/50 hover:bg-white/[0.04]"
                  }`}>
                  <item.icon className="h-5 w-5" />{item.label}
                  {item.badge && <Badge className="ml-auto bg-red-500/20 text-red-400 text-[10px]">{item.badge}</Badge>}
                </button>
              )
            ))}
            <hr className="border-white/[0.06] my-4" />
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10">
              <LogOut className="h-5 w-5" />Logout
            </button>
          </nav>
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col w-60 min-h-screen bg-[#111114] border-r border-white/[0.06] fixed">
          <div className="p-6 pb-4">
            <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#C8A95B]/10 border border-[#C8A95B]/20">
              <span className="text-[#C8A95B] text-[10px] font-medium uppercase tracking-wider">Client Portal</span>
            </div>
          </div>
          <nav className="flex-1 px-3 space-y-0.5">
            {navItems.map(item => {
              const isActive = !item.link && activeTab === item.id;
              return item.link ? (
                <Link key={item.id} to={item.link} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/45 hover:bg-white/[0.04] hover:text-white/75 transition-all" data-testid={`nav-${item.id}`}>
                  <item.icon className="h-[18px] w-[18px]" />{item.label}
                </Link>
              ) : (
                <button key={item.id} onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all ${isActive ? "bg-[#C8A95B]/10 text-[#C8A95B] font-medium" : "text-white/45 hover:bg-white/[0.04] hover:text-white/75"}`}
                  data-testid={`nav-${item.id}`}>
                  <item.icon className="h-[18px] w-[18px]" />{item.label}
                  {item.badge && <Badge className="ml-auto bg-red-500/20 text-red-400 text-[10px]">{item.badge}</Badge>}
                  {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#C8A95B]" />}
                </button>
              );
            })}
          </nav>
          <div className="p-3 border-t border-white/[0.06]">
            <div className="flex items-center gap-3 px-3 py-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-[#C8A95B]/15 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-[#C8A95B]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white/80 truncate">{user?.name}</p>
                {user?.company_name && <p className="text-[11px] text-white/30">{user.company_name}</p>}
              </div>
            </div>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors text-sm" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-60 pt-16 lg:pt-0">
          <div className="p-4 lg:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold">My Dashboard</h1>
                <p className="text-muted-foreground text-sm">Manage your transfers and invoices</p>
              </div>
              <Link to="/booking/new">
                <Button className="bg-[#C8A95B] text-black hover:bg-[#d4b96a] rounded-full font-semibold" data-testid="new-booking-btn">
                  <Plus className="h-4 w-4 mr-2" />Book a Transfer
                </Button>
              </Link>
            </div>

            {/* ===== BOOKINGS TAB ===== */}
            {activeTab === "bookings" && (
              <>
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {[
                    { icon: Calendar, color: "text-[#C8A95B]", bg: "bg-[#C8A95B]/10", label: "Upcoming", value: upcomingBookings.length },
                    { icon: CheckCircle, color: "text-green-500", bg: "bg-green-500/10", label: "Completed", value: bookings.filter(b => b.status === "completed").length },
                    { icon: Clock, color: "text-purple-500", bg: "bg-purple-500/10", label: "Total Bookings", value: bookings.length },
                    { icon: DollarSign, color: "text-blue-500", bg: "bg-blue-500/10", label: "Total Spent", value: `$${bookings.filter(b => b.payment_status === "paid").reduce((s, b) => s + b.price, 0).toFixed(0)}` },
                  ].map((stat, i) => (
                    <Card key={i} className="border border-black/[0.06]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                            <stat.icon className={`h-5 w-5 ${stat.color}`} />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{stat.value}</p>
                            <p className="text-xs text-muted-foreground">{stat.label}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Upcoming Bookings */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold mb-4">Upcoming Transfers</h2>
                  {upcomingBookings.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-black/[0.08] rounded-xl">
                      <Calendar className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground mb-4">No upcoming bookings</p>
                      <Link to="/booking/new">
                        <Button className="bg-[#C8A95B] text-black hover:bg-[#d4b96a] rounded-full">Book a Transfer</Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {upcomingBookings.map(booking => (
                        <Card key={booking.id} className="border border-black/[0.06] overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/booking/${booking.id}`)} data-testid={`booking-card-${booking.id}`}>
                          <div className={`h-0.5 ${
                            booking.status === 'dispatched' ? 'bg-purple-500' :
                            booking.status === 'confirmed' ? 'bg-blue-500' :
                            'bg-yellow-500'
                          }`} />
                          <CardContent className="p-4 lg:p-5">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-mono text-[#C8A95B] font-medium text-sm">{booking.booking_ref}</span>
                                  {getStatusBadge(booking.status)}
                                  {getPaymentBadge(booking.payment_status)}
                                </div>
                                <div className="space-y-1.5 mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                                    <p className="text-sm truncate">{booking.pickup_location}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                                    <p className="text-sm text-muted-foreground truncate">{booking.dropoff_location}</p>
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(booking.pickup_datetime).toLocaleDateString()}</span>
                                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{new Date(booking.pickup_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                  <span className="capitalize">{booking.vehicle_type?.replace(/_/g, ' ')}</span>
                                  {booking.driver_name && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{booking.driver_name}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 pt-3 lg:pt-0 border-t lg:border-t-0 border-black/[0.04]" onClick={e => e.stopPropagation()}>
                                <p className="text-xl font-bold text-[#C8A95B]">${booking.price.toFixed(2)}</p>
                                <div className="flex gap-2">
                                  {booking.payment_status === "pending" && booking.price > 0 && (
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handlePayment(booking.id)} data-testid={`pay-${booking.id}`}>
                                      <CreditCard className="h-3.5 w-3.5 mr-1" />Pay
                                    </Button>
                                  )}
                                  {booking.status === "pending" && (
                                    <Button size="sm" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => handleCancelBooking(booking.id)} data-testid={`cancel-${booking.id}`}>
                                      <XCircle className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                {/* Past */}
                {pastBookings.length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold mb-4">Past Transfers</h2>
                    <div className="space-y-2">
                      {pastBookings.map(booking => (
                        <Card key={booking.id} className="border border-black/[0.06] opacity-60 hover:opacity-100 transition-opacity cursor-pointer" onClick={() => navigate(`/booking/${booking.id}`)}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="font-mono text-[#C8A95B] text-xs">{booking.booking_ref}</span>
                                {getStatusBadge(booking.status)}
                                <span className="text-xs text-muted-foreground hidden sm:inline">{booking.pickup_location} → {booking.dropoff_location}</span>
                                <span className="text-xs text-muted-foreground">{new Date(booking.pickup_datetime).toLocaleDateString()}</span>
                              </div>
                              <p className="font-semibold">${booking.price.toFixed(2)}</p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ===== INVOICES TAB ===== */}
            {activeTab === "invoices" && (
              <div className="space-y-8">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { icon: AlertCircle, color: "text-yellow-500", bg: "bg-yellow-500/10", label: "Pending", value: pendingInvoices.length },
                    { icon: CheckCircle, color: "text-green-500", bg: "bg-green-500/10", label: "Paid", value: paidInvoices.length },
                    { icon: DollarSign, color: "text-red-500", bg: "bg-red-500/10", label: "Amount Due", value: `$${pendingInvoices.reduce((s, i) => s + i.total, 0).toFixed(2)}` },
                  ].map((stat, i) => (
                    <Card key={i} className="border border-black/[0.06]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                            <stat.icon className={`h-5 w-5 ${stat.color}`} />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{stat.value}</p>
                            <p className="text-xs text-muted-foreground">{stat.label}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {pendingInvoices.length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold mb-4">Pending Invoices</h2>
                    <div className="space-y-3">
                      {pendingInvoices.map(invoice => (
                        <Card key={invoice.id} className={`border ${invoice.status === "overdue" ? "border-red-500/30" : "border-black/[0.06]"}`}>
                          <CardContent className="p-4 lg:p-5">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-mono text-[#C8A95B] font-medium">{invoice.invoice_number}</span>
                                  <Badge className={invoice.status === "overdue" ? "bg-red-500/15 text-red-400 border border-red-500/20" : "bg-yellow-500/15 text-yellow-400 border border-yellow-500/20"}>
                                    {invoice.status === "overdue" ? "OVERDUE" : "PENDING"}
                                  </Badge>
                                </div>
                                <div className="flex gap-4 text-xs text-muted-foreground">
                                  <span>Issued: {new Date(invoice.issued_date).toLocaleDateString()}</span>
                                  <span>Due: {new Date(invoice.due_date).toLocaleDateString()}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <p className="text-xl font-bold">${invoice.total.toFixed(2)}</p>
                                <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)} disabled={downloadingPdf === invoice.id} data-testid={`download-${invoice.id}`}>
                                  {downloadingPdf === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                </Button>
                                <Button size="sm" className="bg-[#C8A95B] text-black hover:bg-[#d4b96a]" onClick={() => handleInvoicePayment(invoice.id)} data-testid={`pay-${invoice.id}`}>
                                  <CreditCard className="h-4 w-4 mr-1" />Pay Now
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {paidInvoices.length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold mb-4">Paid Invoices</h2>
                    <div className="space-y-2">
                      {paidInvoices.map(invoice => (
                        <Card key={invoice.id} className="border border-black/[0.06] opacity-60 hover:opacity-100 transition-opacity">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="font-mono text-[#C8A95B] text-xs">{invoice.invoice_number}</span>
                                <Badge className="bg-green-500/15 text-green-400 border border-green-500/20">PAID</Badge>
                                <span className="text-xs text-muted-foreground">Paid: {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString() : "-"}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <p className="font-semibold text-muted-foreground">${invoice.total.toFixed(2)}</p>
                                <Button size="sm" variant="ghost" onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)} disabled={downloadingPdf === invoice.id}>
                                  {downloadingPdf === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {invoices.length === 0 && (
                  <div className="text-center py-16 border border-dashed border-black/[0.08] rounded-xl">
                    <FileText className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">No invoices found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientDashboard;
